import { useQuery } from '@tanstack/react-query'

import { Notice, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@speaking-coach/ui'
import { queryKeys } from '../api/query-keys'
import { request } from '../api/client'

import type { ProgramSummary, ProgramLevelSummary } from '@speaking-coach/shared-types/src/content.js'
export type { ProgramSummary, ProgramLevelSummary } from '@speaking-coach/shared-types/src/content.js'

export interface ProgramOption extends ProgramSummary {
  levels: ProgramLevelSummary[]
  readyForEnrollment: boolean
  readinessBlockers: string[]
  availableLessonCount: number
}

export interface ProgramListResponse { programs: ProgramOption[] }

export const programsApi = {
  list(): Promise<ProgramListResponse> {
    return request<ProgramListResponse>('/coach/admin/content/programs', { method: 'GET' })
  },
}

export function usePrograms() {
  return useQuery({ queryKey: queryKeys.programs, queryFn: programsApi.list })
}

export function ProgramPicker({
  id,
  value,
  currentProgram = null,
  onValueChange,
  invalid = false,
  requireReady = false,
}: {
  id: string
  value: string
  /**
   * Saved context supplied by a cohort or enrollment response. The choices
   * endpoint deliberately contains active Programs only, so this is what lets
   * an operator identify a retained draft/retired value before replacing it.
   */
  currentProgram?: ProgramSummary | null
  onValueChange: (value: string) => void
  invalid?: boolean
  requireReady?: boolean
}): JSX.Element {
  const query = usePrograms()
  // The endpoint promises active rows only. Keep the write control safe if
  // that contract regresses: retained inactive Programs may be displayed as
  // context, but never offered as a new assignment.
  const programs = (query.data?.programs ?? []).filter(
    (program) => program.status === 'active',
  )
  const selectedProgram = programs.find((program) => program.id === value)
  const retainedSelection =
    selectedProgram === undefined &&
    currentProgram?.id === value &&
    currentProgram.status !== 'active'
      ? currentProgram
      : null
  const selectedIsUnavailable = query.isSuccess && value !== '' &&
    selectedProgram === undefined && retainedSelection === null
  const selectedIsInactive =
    retainedSelection !== null && retainedSelection.status !== 'active'
  const selectedIsNotReady =
    requireReady && selectedProgram !== undefined && !selectedProgram.readyForEnrollment
  return (
    <div className="space-y-1">
      <Select value={value} onValueChange={onValueChange} disabled={query.isPending || query.isError}>
        <SelectTrigger
          id={id}
          aria-invalid={
            invalid || selectedIsUnavailable || selectedIsInactive || selectedIsNotReady
          }
        >
          <SelectValue placeholder={query.isPending ? 'Loading programs…' : 'Choose a program'} />
        </SelectTrigger>
        <SelectContent>
          {retainedSelection !== null && (
            <SelectItem value={retainedSelection.id} disabled>
              {retainedSelection.name.en} · {retainedSelection.status} · replacement required
            </SelectItem>
          )}
          {selectedIsUnavailable && <SelectItem value={value} disabled>Unavailable program</SelectItem>}
          {programs.map((program) => (
            <SelectItem key={program.id} value={program.id} disabled={requireReady && !program.readyForEnrollment}>
              {program.name.en} · {program.levels.map((level) => level.cefrLevel).join(', ')}
              {!program.readyForEnrollment && ' · not ready for enrollment'}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {query.isError && <Notice variant="danger">Programs could not be loaded. Reload before saving.</Notice>}
      {query.isSuccess && programs.length === 0 && <Notice variant="warning">No active Program is available. Activate content before creating enrollments.</Notice>}
      {selectedIsInactive && (
        <Notice variant="warning">
          {retainedSelection.name.en} is {retainedSelection.status}. Choose an active Program
          before this cohort can lock new enrollment terms. Existing enrollments keep their
          saved Program and level.
        </Notice>
      )}
      {selectedIsNotReady && (
        <Notice variant="warning">
          {selectedProgram.name.en} is active but not ready for enrollment. Resolve its
          diagnostic, default tutor, and level coverage before locking new terms.
        </Notice>
      )}
      {selectedIsUnavailable && <Notice variant="warning">This Program is no longer available. Choose an active Program.</Notice>}
    </div>
  )
}

/** Choice labels share the same cached global catalog as the picker. */
export function ProgramName({
  programId,
  currentProgram = null,
}: {
  programId: string | undefined
  currentProgram?: ProgramSummary | null
}): JSX.Element {
  const query = usePrograms()
  const retainedName =
    currentProgram !== null && currentProgram.id === programId
      ? currentProgram.name.en
      : null
  return <>{query.data?.programs.find((program) => program.id === programId)?.name.en ??
    retainedName ?? (query.isPending ? 'Loading program…' : 'Program unavailable')}</>
}
